/**
 * Notification API Service
 */

import apiClient from './apiClient';
import authService from './authService';
import { NotificationDTO } from '../types/notification.types';

const BASE_PATH = '/v1/notifications';

class NotificationService {
  async getUserNotifications(userId: string): Promise<NotificationDTO[]> {
    const response = await apiClient.get<NotificationDTO[]>(`${BASE_PATH}/user/${userId}`);
    return response.data;
  }

  async getUnreadNotifications(userId: string): Promise<NotificationDTO[]> {
    const response = await apiClient.get<NotificationDTO[]>(`${BASE_PATH}/user/${userId}/unread`);
    return response.data;
  }

  async getUnreadNotificationCount(userId: string): Promise<number> {
    try {
      const response = await apiClient.get<any>(`${BASE_PATH}/user/${userId}/unread-count`);
      if (typeof response.data === 'number') return response.data;
      if (response.data && typeof response.data.count === 'number') return response.data.count;
      if (response.data && typeof response.data.unreadCount === 'number') return response.data.unreadCount;
      if (response.data && typeof response.data.data === 'number') return response.data.data;
      const num = parseInt(response.data, 10);
      return isNaN(num) ? 0 : num;
    } catch (err: any) {
      if (err?.response?.status === 404) {
        try {
          const unreadResp = await apiClient.get<any>(`${BASE_PATH}/user/${userId}/unread`);
          if (Array.isArray(unreadResp.data)) return unreadResp.data.length;
          if (unreadResp.data?.content && Array.isArray(unreadResp.data.content)) return unreadResp.data.content.length;
        } catch (e) {
          // ignore
        }
      }
      return 0;
    }
  }

  async markAsRead(notificationId: string): Promise<void> {
    await apiClient.patch(`${BASE_PATH}/${notificationId}/read`);
  }

  async markAllAsRead(userId: string): Promise<void> {
    await apiClient.patch(`${BASE_PATH}/user/${userId}/read-all`);
  }

  async getNotificationPreferences(facilityId: string): Promise<any> {
    const response = await apiClient.get(`/v1/notification-preferences?facilityId=${facilityId}`);
    return response.data;
  }

  async updateNotificationPreferences(data: any): Promise<any> {
    // Strictly filter payload to match expected fields from user's curl example
    // This prevents 500 errors caused by sending id, createdAt, etc. in POST/PUT bodies
    const payload = {
      medicationRemindersEnabled: !!data.medicationRemindersEnabled,
      medicationRemindersMins: Number(data.medicationRemindersMins || 0),
      missedShiftAlertsEnabled: !!data.missedShiftAlertsEnabled,
      incidentReportAlertsEnabled: !!data.incidentReportAlertsEnabled,
      facilityId: data.facilityId || authService.getFacilityIdFromToken() || localStorage.getItem("currentTenantId") || ""
    };

    console.log('Sending Notification Preferences Payload:', payload);

    if (data.id) {
       console.log(`Performing PUT update for ID: ${data.id}`);
       const response = await apiClient.put(`/v1/notification-preferences/${data.id}`, payload);
       return response.data;
    }
    
    console.log('Performing POST create');
    const response = await apiClient.post('/v1/notification-preferences', payload);
    return response.data;
  }

  async deleteNotificationPreference(id: string): Promise<any> {
    const response = await apiClient.delete(`/v1/notification-preferences/${id}`);
    return response.data;
  }
}

export default new NotificationService();
